function asyncGeneratorStep(n, t, e, r, o, a, c) { try { var i = n[a](c), u = i.value; } catch (n) { return void e(n); } i.done ? t(u) : Promise.resolve(u).then(r, o); }
function _asyncToGenerator(n) { return function () { var t = this, e = arguments; return new Promise(function (r, o) { var a = n.apply(t, e); function _next(n) { asyncGeneratorStep(a, r, o, _next, _throw, "next", n); } function _throw(n) { asyncGeneratorStep(a, r, o, _next, _throw, "throw", n); } _next(void 0); }); }; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
import React, { PureComponent } from 'react';
import classNames from 'classnames';
import * as monaco from 'monaco-editor';
import Log from '@deephaven/log';
import { assertNotNull, PromiseUtils } from '@deephaven/utils';
import { MonacoProviders, MonacoTheme, MonacoUtils } from "./monaco/index.js";
import "./ConsoleInput.css";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
var log = Log.module('ConsoleInput');
var LINE_HEIGHT = parseInt(MonacoTheme['line-height'], 10);
var TOP_PADDING = 6;
var BOTTOM_PADDING = 6;
var MIN_INPUT_HEIGHT = LINE_HEIGHT + TOP_PADDING + BOTTOM_PADDING;
var BUFFER_SIZE = 100;
/**
 * Component for input in a console session. Handles loading the recent command history
 */
export class ConsoleInput extends PureComponent {
  constructor(props) {
    super(props);
    _defineProperty(this, "cancelListener", void 0);
    _defineProperty(this, "openDocumentCleanup", void 0);
    _defineProperty(this, "resizeObserver", void 0);
    _defineProperty(this, "commandContainer", void 0);
    _defineProperty(this, "commandEditor", void 0);
    _defineProperty(this, "commandHistoryIndex", void 0);
    _defineProperty(this, "commandSuggestionContainer", void 0);
    _defineProperty(this, "loadingPromise", void 0);
    _defineProperty(this, "timestamp", void 0);
    _defineProperty(this, "bufferIndex", void 0);
    _defineProperty(this, "history", void 0);
    // Tracks every command that has been modified by its commandHistoryIndex. Cleared on any command being executed
    _defineProperty(this, "modifiedCommands", void 0);
    this.handleResize = this.handleResize.bind(this);
    this.commandContainer = /*#__PURE__*/React.createRef();
    this.commandHistoryIndex = null;
    this.timestamp = Date.now();
    this.bufferIndex = 0;
    this.history = [];
    // Tracks every command that has been modified by its commandHistoryIndex. Cleared on any command being executed
    this.modifiedCommands = new Map();
    this.resizeObserver = new window.ResizeObserver(this.handleResize);
    this.state = {
      commandEditorHeight: LINE_HEIGHT,
      isFocused: false,
      model: null
    };
  }
  componentDidMount() {
    this.initCommandEditor();
    this.loadMoreHistory();
  }
  componentDidUpdate(prevProps) {
    var session = this.props.session;
    this.layoutEditor();
    // If the session has changed, we need to destroy the old command editor and create a new one for the new session
    if (prevProps.session !== session) {
      this.destroyCommandEditor(prevProps.session);
      this.initCommandEditor();
    }
  }
  componentWillUnmount() {
    this.resizeObserver.disconnect();
    if (this.loadingPromise != null) {
      this.loadingPromise.cancel();
    }
    var session = this.props.session;
    this.destroyCommandEditor(session);
  }
  /**
   * Sets the console text from an external source.
   * Sets commandHistoryIndex to null since the source is not part of the history
   * @param text The text to set in the input
   * @param focus If the input should be focused
   * @param execute If the input should be executed
   * @returns void
   */
  setConsoleText(text) {
    var focus = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : true;
    var execute = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : false;
    if (!text) {
      return;
    }
    log.debug('Command received: ', text);

    // Only set the console text if we're not running this command
    if (!execute) {
      var _this$commandEditor;
      // Need to set commandHistoryIndex before value
      // On value change, modified commands map updates
      this.commandHistoryIndex = null;
      (_this$commandEditor = this.commandEditor) === null || _this$commandEditor === void 0 || _this$commandEditor.setValue(text);
    }
    if (focus) {
      this.focusEnd();
    }
    this.updateDimensions();
    if (execute) {
      this.processCommand(text);
    }
  }
  initCommandEditor() {
    var _this$props = this.props,
      language = _this$props.language,
      session = _this$props.session;
    var model = monaco.editor.createModel('', language, MonacoUtils.generateConsoleUri());
    var commandSettings = {
      copyWithSyntaxHighlighting: false,
      cursorStyle: 'block',
      fixedOverflowWidgets: true,
      folding: false,
      fontFamily: 'Fira Mono',
      glyphMargin: false,
      language,
      lineHeight: LINE_HEIGHT,
      lineDecorationsWidth: 3,
      lineNumbers: 'off',
      minimap: {
        enabled: false
      },
      renderLineHighlight: 'none',
      scrollBeyondLastLine: false,
      scrollbar: {
        arrowSize: 0,
        horizontal: 'hidden',
        horizontalScrollbarSize: 0
      },
      padding: {
        top: TOP_PADDING,
        bottom: BOTTOM_PADDING
      },
      value: '',
      wordWrap: 'on',
      autoClosingBrackets: 'beforeWhitespace',
      model
    };
    var element = this.commandContainer.current;
    assertNotNull(element);
    this.commandEditor = monaco.editor.create(element, commandSettings);
    MonacoUtils.setEOL(this.commandEditor);
    this.openDocumentCleanup = MonacoUtils.openDocument(this.commandEditor, session);
    this.commandEditor.onDidChangeModelContent(() => {
      var _this$commandEditor2;
      var value = (_this$commandEditor2 = this.commandEditor) === null || _this$commandEditor2 === void 0 ? void 0 : _this$commandEditor2.getValue();
      this.modifiedCommands.set(this.commandHistoryIndex, value !== null && value !== void 0 ? value : null);
      this.updateDimensions();
    });
    this.commandEditor.onDidFocusEditorText(() => {
      this.setState({
        isFocused: true
      });
    });
    this.commandEditor.onDidBlurEditorText(() => {
      this.setState({
        isFocused: false
      });
    });

    /**
     * Register for keydown events to capture the `Enter` key.
     * Need to do it this way instead of using `addCommand`, because that would eat the Enter key in all situations, which is not what we want.
     * Can't do it in `onDidChangeModelContent` either, since we want to stop the Enter action from modifying the command.
     */
    this.commandEditor.onKeyDown(keyEvent => {
      var commandEditor = this.commandEditor,
        commandHistoryIndex = this.commandHistoryIndex;
      var position = commandEditor === null || commandEditor === void 0 ? void 0 : commandEditor.getPosition();
      assertNotNull(position);
      var lineNumber = position.lineNumber;
      if (!keyEvent.altKey && !keyEvent.shiftKey && !keyEvent.metaKey) {
        if (keyEvent.code === 'ArrowUp' && !this.isSuggestionMenuPopulated() && lineNumber === 1) {
          if (commandHistoryIndex != null) {
            this.loadCommand(commandHistoryIndex + 1);
          } else {
            this.loadCommand(0);
          }
          this.focusStart();
          keyEvent.stopPropagation();
          keyEvent.preventDefault();
          return;
        }
        if (keyEvent.code === 'ArrowDown' && !this.isSuggestionMenuPopulated() && lineNumber === (model === null || model === void 0 ? void 0 : model.getLineCount())) {
          if (commandHistoryIndex != null && commandHistoryIndex > 0) {
            this.loadCommand(commandHistoryIndex - 1);
          } else {
            this.loadCommand(null);
          }
          this.focusEnd();
          keyEvent.stopPropagation();
          keyEvent.preventDefault();
          return;
        }
        if (keyEvent.keyCode === monaco.KeyCode.Enter && !this.isSuggestionMenuPopulated()) {
          var _this$commandEditor3;
          keyEvent.stopPropagation();
          keyEvent.preventDefault();
          var command = (_this$commandEditor3 = this.commandEditor) === null || _this$commandEditor3 === void 0 ? void 0 : _this$commandEditor3.getValue().trim();
          if (command !== undefined) {
            var _this$commandEditor4;
            this.processCommand(command);
            (_this$commandEditor4 = this.commandEditor) === null || _this$commandEditor4 === void 0 || _this$commandEditor4.setValue('');
          }
        }
      }
    });

    // Disable the Ctrl+F functionality so that the find window doesn't appear
    MonacoUtils.disableKeyBindings(this.commandEditor, [monaco.KeyMod.CtrlCmd | monaco.KeyCode.KeyF // eslint-disable-line no-bitwise
    ]);
    MonacoUtils.registerPasteHandler(this.commandEditor);
    this.commandEditor.focus();
    this.resizeObserver.observe(element);
    this.updateDimensions();
    this.setState({
      model: this.commandEditor.getModel()
    });
  }

  /**
   * Closes the monaco document, disposes the Monaco editor, and clears all associated cleanup state.
   *
   * @param session The session that originally received `openDocument` for this editor
   */
  destroyCommandEditor(session) {
    if (this.commandEditor) {
      var _this$openDocumentCle;
      (_this$openDocumentCle = this.openDocumentCleanup) === null || _this$openDocumentCle === void 0 || _this$openDocumentCle.dispose();
      this.openDocumentCleanup = undefined;
      MonacoUtils.closeDocument(this.commandEditor, session);
      this.commandEditor.dispose();
      this.commandEditor = undefined;
    }
  }
  handleResize() {
    this.updateDimensions();
  }
  isSuggestionMenuActive() {
    var _ref;
    if (!this.commandSuggestionContainer) {
      var _this$commandEditor5;
      this.commandSuggestionContainer = (_this$commandEditor5 = this.commandEditor) === null || _this$commandEditor5 === void 0 || (_this$commandEditor5 = _this$commandEditor5.getDomNode()) === null || _this$commandEditor5 === void 0 ? void 0 : _this$commandEditor5.querySelector('.suggest-widget');
    }
    return (_ref = this.commandSuggestionContainer && this.commandSuggestionContainer.classList.contains('visible')) !== null && _ref !== void 0 ? _ref : false;
  }
  isSuggestionMenuPopulated() {
    var _this$commandSuggesti, _this$commandSuggesti2;
    return this.isSuggestionMenuActive() && ((_this$commandSuggesti = (_this$commandSuggesti2 = this.commandSuggestionContainer) === null || _this$commandSuggesti2 === void 0 || (_this$commandSuggesti2 = _this$commandSuggesti2.querySelector('.monaco-list-rows')) === null || _this$commandSuggesti2 === void 0 ? void 0 : _this$commandSuggesti2.childElementCount) !== null && _this$commandSuggesti !== void 0 ? _this$commandSuggesti : 0) > 0;
  }
  focus() {
    var _this$commandEditor6;
    (_this$commandEditor6 = this.commandEditor) === null || _this$commandEditor6 === void 0 || _this$commandEditor6.focus();
  }
  focusStart() {
    var _this$commandEditor7, _this$commandEditor8, _this$commandEditor9, _this$commandEditor0, _this$commandEditor1;
    var model = (_this$commandEditor7 = this.commandEditor) === null || _this$commandEditor7 === void 0 ? void 0 : _this$commandEditor7.getModel();
    assertNotNull(model);
    var column = model.getLineLength(1) + 1; // Length of 1st line
    var firstCharTop = (_this$commandEditor8 = this.commandEditor) === null || _this$commandEditor8 === void 0 ? void 0 : _this$commandEditor8.getTopForPosition(1, column);
    assertNotNull(firstCharTop);
    (_this$commandEditor9 = this.commandEditor) === null || _this$commandEditor9 === void 0 || _this$commandEditor9.setPosition({
      lineNumber: 1,
      column
    });
    (_this$commandEditor0 = this.commandEditor) === null || _this$commandEditor0 === void 0 || _this$commandEditor0.setScrollTop(firstCharTop);
    (_this$commandEditor1 = this.commandEditor) === null || _this$commandEditor1 === void 0 || _this$commandEditor1.focus();
  }
  focusEnd() {
    var _this$commandEditor10, _this$commandEditor11, _this$commandEditor12, _this$commandEditor13, _this$commandEditor14;
    var model = (_this$commandEditor10 = this.commandEditor) === null || _this$commandEditor10 === void 0 ? void 0 : _this$commandEditor10.getModel();
    assertNotNull(model);
    var lastLine = model.getLineCount();
    var column = model.getLineLength(lastLine) + 1;
    var lastCharTop = (_this$commandEditor11 = this.commandEditor) === null || _this$commandEditor11 === void 0 ? void 0 : _this$commandEditor11.getTopForPosition(lastLine, column);
    assertNotNull(lastCharTop);
    (_this$commandEditor12 = this.commandEditor) === null || _this$commandEditor12 === void 0 || _this$commandEditor12.setPosition({
      lineNumber: lastLine,
      column
    });
    (_this$commandEditor13 = this.commandEditor) === null || _this$commandEditor13 === void 0 || _this$commandEditor13.setScrollTop(lastCharTop);
    (_this$commandEditor14 = this.commandEditor) === null || _this$commandEditor14 === void 0 || _this$commandEditor14.focus();
  }
  clear() {
    var _this$commandEditor15, _this$commandEditor16;
    (_this$commandEditor15 = this.commandEditor) === null || _this$commandEditor15 === void 0 || _this$commandEditor15.focus();
    (_this$commandEditor16 = this.commandEditor) === null || _this$commandEditor16 === void 0 || (_this$commandEditor16 = _this$commandEditor16.getModel()) === null || _this$commandEditor16 === void 0 || _this$commandEditor16.setValue('');
    this.commandHistoryIndex = null;
  }
  layoutEditor() {
    if (this.commandEditor) {
      this.commandEditor.layout();
    }
  }

  /**
   * Loads the given command from history
   * If edits have been made to the command since last run command, loads the modified version
   * @param index The index to load. Null to load command started in the editor and not in the history
   */
  loadCommand(index) {
    var _this$commandEditor17;
    if (index !== null && index >= this.history.length) {
      return;
    }
    var modifiedValue = this.modifiedCommands.get(index);
    var historyValue = index === null ? '' : this.history[this.history.length - index - 1];
    this.commandHistoryIndex = index;
    (_this$commandEditor17 = this.commandEditor) === null || _this$commandEditor17 === void 0 || (_this$commandEditor17 = _this$commandEditor17.getModel()) === null || _this$commandEditor17 === void 0 || _this$commandEditor17.setValue(modifiedValue !== null && modifiedValue !== void 0 ? modifiedValue : historyValue);
    if (index !== null && index > this.history.length - BUFFER_SIZE) {
      this.loadMoreHistory();
    }
  }
  loadMoreHistory() {
    var _this = this;
    return _asyncToGenerator(function* () {
      try {
        if (_this.loadingPromise != null || _this.bufferIndex == null) {
          return;
        }
        var _this$props2 = _this.props,
          commandHistoryStorage = _this$props2.commandHistoryStorage,
          language = _this$props2.language,
          scope = _this$props2.scope;
        _this.loadingPromise = PromiseUtils.makeCancelable(commandHistoryStorage.getTable(language, scope !== null && scope !== void 0 ? scope : '', _this.timestamp), resolved => resolved.close());
        var table = yield _this.loadingPromise;
        table.setReversed(true);
        table.setViewport({
          top: _this.bufferIndex,
          bottom: _this.bufferIndex + BUFFER_SIZE - 1
        });
        _this.loadingPromise = PromiseUtils.makeCancelable(table.getViewportData(), () => table.close());
        var viewportData = yield _this.loadingPromise;
        _this.bufferIndex += BUFFER_SIZE;
        if (_this.bufferIndex >= table.size) {
          // We've loaded the full history, no need to load any more
          _this.bufferIndex = null;
        }
        _this.history = [...viewportData.items.filter((_ref2, pos, arr) => {
          var name = _ref2.name;
          return pos === 0 || name !== arr[pos - 1].name;
        }).map(_ref3 => {
          var name = _ref3.name;
          return name;
        }).reverse(), ..._this.history];
        _this.loadingPromise = undefined;
        table.close();
      } catch (err) {
        _this.loadingPromise = undefined;
        if (PromiseUtils.isCanceled(err)) {
          log.debug2('Promise canceled, not loading history');
          return;
        }
        log.error('Error fetching history', err);
      }
    })();
  }
  processCommand(command) {
    this.commandHistoryIndex = null;
    this.modifiedCommands.clear();
    assertNotNull(command);
    if (command !== '' && (this.history.length === 0 || command !== this.history[this.history.length - 1])) {
      this.history.push(command);
    }
    this.updateDimensions();
    var onSubmit = this.props.onSubmit;
    onSubmit(command);
  }
  updateDimensions() {
    if (!this.commandEditor) {
      return;
    }
    var maxHeight = this.props.maxHeight;
    assertNotNull(maxHeight);
    var contentHeight = this.commandEditor.getContentHeight();
    var commandEditorHeight = Math.max(Math.min(contentHeight, maxHeight), MIN_INPUT_HEIGHT);

    // Only show the overview ruler (markings overlapping sroll bar area) if the scrollbar will show
    var shouldScroll = contentHeight > commandEditorHeight;
    var options = {
      overviewRulerLanes: shouldScroll ? undefined : 0
    };
    this.setState({
      commandEditorHeight
    }, () => {
      var _this$commandEditor18, _this$commandEditor19;
      (_this$commandEditor18 = this.commandEditor) === null || _this$commandEditor18 === void 0 || _this$commandEditor18.updateOptions(options);
      (_this$commandEditor19 = this.commandEditor) === null || _this$commandEditor19 === void 0 || _this$commandEditor19.layout();
    });
  }
  render() {
    var _this$props3 = this.props,
      disabled = _this$props3.disabled,
      language = _this$props3.language,
      session = _this$props3.session;
    var _this$state = this.state,
      commandEditorHeight = _this$state.commandEditorHeight,
      isFocused = _this$state.isFocused,
      model = _this$state.model;
    return /*#__PURE__*/_jsx("div", {
      className: classNames('console-input-wrapper', {
        disabled
      }),
      children: /*#__PURE__*/_jsxs("div", {
        className: classNames('console-input-inner-wrapper', {
          focus: isFocused
        }),
        children: [/*#__PURE__*/_jsx("div", {
          className: ConsoleInput.INPUT_CLASS_NAME,
          ref: this.commandContainer,
          style: {
            height: commandEditorHeight
          }
        }), model && /*#__PURE__*/_jsx(MonacoProviders, {
          model: model,
          language: language,
          session: session
        })]
      })
    });
  }
}
_defineProperty(ConsoleInput, "defaultProps", {
  maxHeight: LINE_HEIGHT * 10,
  scope: null,
  disabled: false
});
_defineProperty(ConsoleInput, "INPUT_CLASS_NAME", 'console-input');
export default ConsoleInput;
//# sourceMappingURL=ConsoleInput.js.map